import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { getQuestionSet } from "@/api/question-sets";
import { buildPlaylist } from "./playlist";
import { progressStore } from "./progress-store";
import { Player, type PlayItem } from "./player";
import { Alert, Button, Spinner } from "@/components/ui";

/** Single-set practice from Library. Builds a playlist and hands it to the
 *  shared Player (the same component Train uses for composed multi-set play). */
export function PlaySession() {
  const { t } = useTranslation();
  const { setId = "" } = useParams();
  const [params] = useSearchParams();

  const setQuery = useQuery({
    queryKey: ["question-set", setId],
    queryFn: () => getQuestionSet(setId),
    enabled: !!setId,
  });

  const items: PlayItem[] = useMemo(() => {
    if (!setQuery.data) return [];
    return buildPlaylist(setQuery.data).map((e) => ({
      item: e.item,
      setId,
      topicSlug: e.topicSlug,
      frameworkSlug: e.frameworkSlug,
    }));
  }, [setQuery.data, setId]);

  // "Continue where you left off": resume at the question after the saved cursor.
  const startIndex = useMemo(() => {
    if (!params.has("resume") || items.length === 0) return 0;
    const cursor = progressStore.cursor(setId);
    if (!cursor) return 0;
    const idx = items.findIndex((e) => e.item.id === cursor.lastQuestionId);
    return idx < 0 ? 0 : Math.min(idx + 1, items.length - 1);
  }, [params, items, setId]);

  if (setQuery.isLoading) {
    return (
      <div className="flex justify-center py-24 text-brand-600">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }
  if (setQuery.isError) {
    return (
      <div className="mx-auto max-w-lg">
        <Alert>{setQuery.error instanceof Error ? setQuery.error.message : t("Couldn't load set.")}</Alert>
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <p className="text-ink-faint">{t("This set has no practisable questions yet.")}</p>
        <Link to={`/app/library/${setId}`} className="mt-4 inline-block">
          <Button variant="secondary">{t("Back to set")}</Button>
        </Link>
      </div>
    );
  }

  return (
    <Player
      items={items}
      anchorSetId={setId}
      startIndex={startIndex}
      backTo={`/app/library/${setId}`}
      againTo={`/app/library/${setId}/play`}
    />
  );
}
